'juse client';

import React from 'react';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import type { ClassValue, VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

type ToggleGroupItemProps = {
  children?: React.ReactNode;
} & ToggleGroupPrimitive.ToggleGroupItemProps;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>((props, ref) => {
  return (
    <ToggleGroupPrimitive.Item
      className={cn(props.className)}
      ref={ref}
      {...props}
    >
      {props.children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

const toggleGroupVariants = tv({
  slots: {
    root: 'flex h-10 w-fit items-center justify-start gap-2 rounded p-1',
    item: `relative capitalize item-center align-middle text-center flex h-8 w-full justify-center
    gap-1 rounded-sm text-sm font-medium text-primary-500 hover:bg-primary-100 data-[state=on]:text-base-white data-[state=on]:bg-primary-500
    `,
  },
  variants: {
    background: {
      default: {
        root: 'bg-primary-100 data-[embedded=true]:bg-primary-50',
      },
      transparent: {
        root: 'bg-transparent',
      },
    },
    variant: {
      text: {
        item: 'py-[0.375rem] px-2',
      },
      'with-icon': {
        item: 'pl-1 py-[0.375rem] pr-2',
      },
      'icon-only': {
        item: 'p-[0.375rem]',
      },
      button: {
        item: 'py-2 px-3 h-fit border hover:bg-transparent data-[disabled=false]:hover:border-primary-300 rounded-sm text-gray-900 data-[state=on]:bg-primary-100 data-[state=on]:border-primary data-[state=on]:text-gray-900',
        root: 'gap-x-4',
      },
    },
  },
  defaultVariants: {
    background: 'default',
    variant: 'text',
  },
});

export type ToggleGroupProps = {
  options: {
    value: string;
    label?: string;
    icon?: JSX.Element;
    badge?: JSX.Element;
  }[];
  classNames?: {
    root?: ClassValue;
    item?: ClassValue;
  };
  isEmbedded?: boolean;
  dataTestIdMap?: Record<string, string>;
} & ToggleGroupPrimitive.ToggleGroupSingleProps &
  VariantProps<typeof toggleGroupVariants>;

export const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>((props, ref) => {
  const { root, item: itemStyles } = toggleGroupVariants({
    variant: props.variant,
    background: props.background,
  });

  const showIcon =
    props.variant === 'with-icon' || props.variant === 'icon-only';
  const showLabel =
    props.variant === 'text' ||
    props.variant === 'with-icon' ||
    props.variant === 'button';

  return (
    <ToggleGroupPrimitive.Root
      className={root({
        className: props.classNames?.root,
      })}
      ref={ref}
      data-dd-privacy="allow"
      data-embedded={props.isEmbedded}
      {...props}
    >
      {props.options.map((option) => {
        return (
          <ToggleGroupItem
            data-disabled={props.disabled}
            data-embedded={props.isEmbedded}
            data-testid={props.dataTestIdMap?.[option.value]}
            className={itemStyles({
              className: props.classNames?.item,
            })}
            key={option.value}
            value={option.value}
          >
            {showIcon ? option.icon : null}
            {showLabel ? option.label : null}
            {option.badge ? option.badge : null}
          </ToggleGroupItem>
        );
      })}
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Item.displayName;
